/**
 * useCreateChallengeRealmSignin — codeless RTTR sign-in.
 *
 * Calls POST /challenges/:slug/realm-signin, which authenticates the visitor off
 * the signed Realm embed (no verification code) and returns a DevourPlay session
 * token. The X-Realm-UUID/TS/Sig headers are attached automatically by
 * `getConfig` from rttrStore; the generated method also takes them as named
 * arguments (swagger declares them `in: header, required: true`), so we pass the
 * same Redux values to satisfy the signature — mirroring useGetChallengeMemberStatus.
 *
 * On success the caller dispatches `login(token)` to store the session, then
 * proceeds to POST /register with terms + declared geo (the existing
 * useCreateChallengeRegistration) — realm-signin deliberately does NOT record
 * terms/geo or bind the realmUuid; /register owns that.
 *
 * A 422 response means Realm has no email on file for this UUID — the caller
 * catches it and falls back to the standard email + verification-code flow
 * (ChallengeLoginModal). That is the only path that still uses a code.
 */
import { useMutation } from "@tanstack/react-query";
import { ChallengesApi, Token } from "@devour/client";
import { useSelector } from "react-redux";
import getConfig from "@/utils/getConfig";
import { IStore } from "@/redux/defaultStore";

interface CreateChallengeRealmSigninVars {
    slug: string;
}

export function useCreateChallengeRealmSignin() {
    const realmUuid = useSelector((store: IStore) => store.rttrStore?.realmUuid);
    const realmTs = useSelector((store: IStore) => store.rttrStore?.realmTs);
    const realmSig = useSelector((store: IStore) => store.rttrStore?.realmSig);

    return useMutation({
        mutationFn: async ({ slug }: CreateChallengeRealmSigninVars): Promise<Token> => {
            const api = new ChallengesApi(getConfig());
            return api.createChallengesSlugRealmSignin({
                slug,
                xRealmUUID: realmUuid!,
                xRealmTS: realmTs!,
                xRealmSig: realmSig!,
            });
        },
    });
}
